import React, { useEffect, useRef } from "react";
import { motion, useAnimation, useInView } from "framer-motion";
import { ChevronDown, Target, Book, Lightbulb, UserPlus, Share2, Award, Heart, Clock, Facebook, Instagram, Linkedin, Twitter } from "lucide-react";
import backgroundImage from "../assets/about-background.jpg";
import teamMember1Image from "../assets/team1.webp";
import { Link } from "react-router-dom";

const About = () => {
  // Animation controls for scroll-triggered animations
  const controls = useAnimation();
  const ref = useRef(null);
  const inView = useInView(ref, { once: false, threshold: 0.3 });

  useEffect(() => {
    if (inView) {
      controls.start("visible");
    }
  }, [controls, inView]);

  // Custom animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: [0.6, 0.05, 0.01, 0.9]
      }
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  return (
    <div className="overflow-hidden">
      {/* Enhanced Hero Section with Video Background */}
      <section className="relative min-h-screen pt-20">
        {/* Animated Background with Parallax Effect */}
        <motion.div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${backgroundImage})` }}
          initial={{ scale: 1.1 }}
          animate={{ scale: 1 }}
          transition={{ duration: 20, repeat: Infinity, repeatType: "reverse", ease: "easeInOut" }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-green-900/80 via-green-800/70 to-emerald-900/80 backdrop-blur-sm"></div>
        </motion.div>

        {/* Animated Particles/Dots Overlay - Adding subtle gold particles */}
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(20)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute rounded-full bg-white/30 backdrop-blur-md"
              style={{
                width: Math.random() * 60 + 20,
                height: Math.random() * 60 + 20,
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                backgroundColor: i % 5 === 0 ? 'rgba(255, 215, 0, 0.2)' : 'rgba(255, 255, 255, 0.3)'
              }}
              initial={{ opacity: 0.1 }}
              animate={{
                opacity: [0.1, 0.3, 0.1],
                scale: [1, 1.2, 1]
              }}
              transition={{
                duration: Math.random() * 5 + 5,
                repeat: Infinity,
                repeatType: "reverse",
                ease: "easeInOut",
                delay: Math.random() * 5
              }}
            />
          ))}
        </div>

        {/* Hero Content */}
        <div className="relative flex flex-col items-center justify-center min-h-[85vh] px-4 py-12 text-white">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="text-center max-w-4xl mx-auto"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-6xl md:text-8xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-white via-green-200 to-yellow-100 drop-shadow-lg">
                About Us
              </h1>
            </motion.div>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="text-xl md:text-2xl mb-10 text-green-50 font-light max-w-3xl mx-auto leading-relaxed"
            >
              We are committed to <span className="font-semibold text-yellow-100">mentoring</span>, <span className="font-semibold text-white">developing talent</span>, and <span className="font-semibold text-yellow-100">shaping the next generation</span> of impactful leaders through structured guidance, skills training, and leadership programs.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              <motion.button
                whileHover={{ scale: 1.05, boxShadow: "0 0 25px rgba(255, 215, 0, 0.3)" }}
                whileTap={{ scale: 0.95 }}
                className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-10 py-4 rounded-full font-semibold shadow-xl hover:from-green-600 hover:to-emerald-700 transition-all text-lg border border-yellow-400/30"
              >
                <Link to="/contact" className="flex items-center gap-2">
                  Start Your Journey
                  <motion.span
                    animate={{ x: [0, 5, 0] }}
                    transition={{ repeat: Infinity, duration: 1.5 }}
                    className="text-yellow-200"
                  >
                    →
                  </motion.span>
                </Link>
              </motion.button>
            </motion.div>
          </motion.div>

          {/* Scroll Indicator */}
          <motion.div
            animate={{ y: [0, 12, 0] }}
            transition={{ repeat: Infinity, duration: 1.5 }}
            className="absolute bottom-12 cursor-pointer"
          >
            <motion.div
              whileHover={{ scale: 1.2, y: -5 }}
              whileTap={{ scale: 0.9 }}
              className="p-3 rounded-full bg-white/10 backdrop-blur-md border border-yellow-400/20"
            >
              <ChevronDown className="w-8 h-8 text-yellow-100" />
            </motion.div>
          </motion.div>
        </div>
      </section>


      {/* Story Section with Enhanced Visual Elements */}
      <section className="py-20 px-6 sm:px-10 lg:px-20 relative overflow-hidden bg-white" ref={ref}>
        <div className="absolute inset-0 opacity-5 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAzNGM0LjQxOCAwIDgtMy41ODIgOC04cy0zLjU4Mi04LTgtOC04IDMuNTgyLTggOCAzLjU4MiA4IDggOHoiIHN0cm9rZT0iIzAwMCIgc3Ryb2tlLXdpZHRoPSIyIi8+PC9nPjwvc3ZnPg==')]"></div>

        <motion.div
          className="container mx-auto max-w-5xl relative"
          initial="hidden"
          animate={controls}
          variants={staggerContainer}
        >
          <motion.div variants={fadeInUp} className="text-center mb-12 sm:mb-16">
            <span className="inline-block px-4 py-1 bg-green-100 text-green-700 rounded-full text-sm font-semibold mb-4 border-b-2 border-yellow-400/30">
              OUR JOURNEY
            </span>
            <h2 className="text-4xl sm:text-5xl font-bold text-green-800 mb-4 sm:mb-6">
              Our Story
            </h2>
            <div className="w-16 sm:w-20 h-1 bg-gradient-to-r from-green-500 to-yellow-400 mx-auto rounded-full mb-6"></div>
          </motion.div>

          <motion.div
            variants={fadeInUp}
            className="relative bg-white p-6 sm:p-10 md:p-12 rounded-2xl shadow-xl border border-gray-100"
          >
            <div className="absolute top-0 right-0 w-28 sm:w-40 h-28 sm:h-40 bg-yellow-100/50 rounded-full -mt-8 sm:-mt-10 -mr-8 sm:-mr-10 opacity-50 blur-2xl"></div>
            <div className="absolute bottom-0 left-0 w-40 sm:w-60 h-40 sm:h-60 bg-green-50 rounded-full -mb-12 sm:-mb-20 -ml-12 sm:-ml-20 opacity-50 blur-3xl"></div>

            <p className="text-lg sm:text-xl text-gray-700 leading-relaxed relative z-10 text-justify lg:max-w-3xl mx-auto">
              <span className="text-3xl font-serif text-yellow-600">L</span>ed2Lead Consulting Limited is a forward-thinking consulting firm committed to empowering individuals and organizations to reach their highest potential. We specialize in providing expert mentoring, strategic talent management, and leadership development solutions tailored to meet the unique needs of our clients.

              <span className="block mt-4 sm:mt-6">
                We focus on the growth and development of both people and processes, helping create a strong foundation for sustained success. Whether it's guiding emerging leaders, enhancing workforce capabilities, or driving organizational excellence, Led2Lead is dedicated to transforming potential into performance.
              </span>
            </p>

            <div className="mt-8 sm:mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
              {[
                { icon: <Clock className="w-6 h-6 text-yellow-600" />, text: "Established in 2024" },
                { icon: <Award className="w-6 h-6 text-green-600" />, text: "Industry recognized" },
                { icon: <Heart className="w-6 h-6 text-yellow-600" />, text: "Client-focused approach" }
              ].map((item, index) => (
                <motion.div
                  key={index}
                  className="flex items-center gap-3 p-3 bg-green-50 rounded-lg border-l-2 border-yellow-400/30"
                  initial={{ opacity: 0, y: 20 }}
                  animate={controls}
                  variants={{
                    visible: { opacity: 1, y: 0, transition: { delay: 0.6 + (index * 0.1) } }
                  }}
                >
                  {item.icon}
                  <span className="text-sm sm:text-base font-medium text-gray-700">{item.text}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </motion.div>
      </section>


      {/* Mission & Vision Cards with Enhanced Design */}
      <section className="py-20 px-4 bg-gradient-to-b from-green-50 to-white">
        <motion.div
          className="container mx-auto max-w-6xl"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={staggerContainer}
        >
          <motion.div variants={fadeInUp} className="text-center mb-16">
            <span className="inline-block px-4 py-1 bg-green-100 text-green-700 rounded-full text-sm font-semibold mb-4 border-b-2 border-yellow-400/30">OUR PURPOSE</span>
            <h2 className="text-5xl font-bold text-green-800 mb-6">Our Vision & Mission</h2>
            <div className="w-20 h-1 bg-gradient-to-r from-green-500 to-yellow-400 mx-auto rounded-full"></div>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-10">
            {[
              {
                title: "Our Vision",
                icon: <Target className="w-16 h-16 text-white" />,
                content: "To be known as one of the most sought-after mentorship and talent management consulting organizations globally. Whose quality products becomes its reference point, intentionally positioned to nurture a community of like minds strategically prepared to fill the intellectual, social and network gap of the next generation"
              },
              {
                title: "Our Mission",
                icon: <Book className="w-16 h-16 text-white" />,
                content: "At L2L, one of our key watchwords will be dedication. Dedication to quality service delivery to our clients, retain their trust and increase our returning customers and enjoy the benefits of referrals. Our company will be a leading brand for Champion makers, nurturing and empowering today's young for the responsibilities of tomorrow."
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                className="group relative overflow-hidden rounded-2xl"
                variants={fadeInUp}
                whileHover={{ y: -5 }}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-green-600 to-emerald-800 transform transition-all duration-500 group-hover:scale-105"></div>
                <div className="absolute inset-0 bg-gradient-to-br from-transparent to-yellow-600/20 opacity-60 group-hover:opacity-80 transition-opacity duration-500"></div>
                <div className="absolute top-0 left-0 w-32 h-32 bg-yellow-400/10 rounded-full -mt-10 -ml-10"></div>
                <div className="absolute bottom-0 right-0 w-32 h-32 bg-white/10 rounded-full -mb-10 -mr-10"></div>

                <div className="relative p-10 md:p-12 h-full flex flex-col">
                  <div className="bg-white/10 p-5 rounded-2xl w-20 h-20 flex items-center justify-center mb-6 backdrop-blur-sm border border-yellow-400/30">
                    {item.icon}
                  </div>
                  <h3 className="text-3xl font-bold text-white mb-6">{item.title}</h3>
                  <p className="text-green-50 text-justify text-lg leading-relaxed flex-grow">{item.content}</p>
                  <motion.div
                    className="w-12 h-12 rounded-full bg-white/30 backdrop-blur-sm mt-6 flex items-center justify-center"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                  >
                    <div className="w-10 h-10 rounded-full bg-yellow-400/30 flex items-center justify-center">
                      <div className="w-6 h-6 rounded-full bg-yellow-300/80"></div>
                    </div>
                  </motion.div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Our Approach Section with Interactive Cards */}
      <section className="py-20 px-4">
        <motion.div
          className="container mx-auto max-w-6xl"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={staggerContainer}
        >
          <motion.div variants={fadeInUp} className="text-center mb-16">
            <span className="inline-block px-4 py-1 bg-green-100 text-green-700 rounded-full text-sm font-semibold mb-4 border-b-2 border-yellow-400/30">OUR METHODS</span>
            <h2 className="text-5xl font-bold text-green-800 mb-6">Our Approach</h2>
            <div className="w-20 h-1 bg-gradient-to-r from-green-500 to-yellow-400 mx-auto rounded-full"></div>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: "Mentorship",
                icon: <UserPlus className="w-12 h-12 text-green-500 group-hover:text-white transition-colors duration-300" />,
                content: "Providing structured guidance from industry leaders to mentees, ensuring personalized growth and development."
              },
              {
                title: "Talent Development",
                icon: <Lightbulb className="w-12 h-12 text-green-500 group-hover:text-white transition-colors duration-300" />,
                content: "Enhancing skills and fostering personal and professional growth through targeted programs and training."
              },
              {
                title: "Networking",
                icon: <Share2 className="w-12 h-12 text-green-500 group-hover:text-white transition-colors duration-300" />,
                content: "Creating a strong community of professionals and industry experts to facilitate valuable connections."
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                className="group bg-white border border-gray-100 p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 hover:bg-green-600 text-center relative overflow-hidden"
                variants={fadeInUp}
                whileHover={{ y: -10 }}
              >
                <div className="absolute top-0 left-0 w-full h-1 bg-yellow-500 transform origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></div>
                <div className="absolute bottom-0 right-0 w-full h-1 bg-green-500 transform origin-right scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></div>
                <div className="absolute left-0 top-0 w-1 h-full bg-green-500 transform origin-top scale-y-0 group-hover:scale-y-100 transition-transform duration-300"></div>
                <div className="absolute right-0 bottom-0 w-1 h-full bg-yellow-500 transform origin-bottom scale-y-0 group-hover:scale-y-100 transition-transform duration-300"></div>

                <div className="flex flex-col items-center relative z-10">
                  <div className="mb-6 transform transition-transform duration-300 group-hover:scale-110">
                    {item.icon}
                  </div>
                  <h3 className="text-2xl font-semibold text-green-700 mb-4 group-hover:text-white transition-colors duration-300">{item.title}</h3>
                  <p className="text-gray-600 group-hover:text-white/90 text-justify transition-colors duration-300">{item.content}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Lead Mentor Section with Dynamic Elements */}
      <section className="py-20 px-4 bg-gradient-to-br from-green-50 to-emerald-50/50 relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAzNGM0LjQxOCAwIDgtMy41ODIgOC04cy0zLjU4Mi04LTgtOC04IDMuNTgyLTggOCAzLjU4MiA4IDggOHoiIHN0cm9rZT0iIzAwMCIgc3Ryb2tlLXdpZHRoPSIyIiBvcGFjaXR5PSIwLjA1Ii8+PC9nPjwvc3ZnPg==')] opacity-30"></div>

        <motion.div
          className="container mx-auto max-w-5xl text-center relative"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          variants={staggerContainer}
        >
          <motion.div variants={fadeInUp} className="text-center mb-16">
            <span className="inline-block px-4 py-1 bg-green-100 text-green-700 rounded-full text-sm font-semibold mb-4 border-b-2 border-yellow-400/30">LEADERSHIP</span>
            <h2 className="text-5xl font-bold text-green-800 mb-6">Meet Our Lead Mentor</h2>
            <div className="w-20 h-1 bg-gradient-to-r from-green-500 to-yellow-400 mx-auto rounded-full"></div>
          </motion.div>

          <motion.div
            className="bg-white p-8 md:p-12 rounded-2xl shadow-xl relative overflow-hidden"
            variants={fadeInUp}
          >
            <div className="absolute top-0 right-0 w-60 h-60 bg-yellow-100/50 rounded-full -mt-20 -mr-20 opacity-30"></div>
            <div className="absolute bottom-0 left-0 w-60 h-60 bg-green-50 rounded-full -mb-20 -ml-20 opacity-50"></div>

            <div className="md:flex items-start gap-12 relative z-10">
              <div className="md:w-1/3 mb-8 md:mb-0 flex flex-col items-center">
                <div className="relative w-56 h-56 mx-auto mb-8">
                  <div className="absolute inset-0 rounded-full bg-gradient-to-br from-green-400 to-yellow-400 opacity-30 blur-xl transform -translate-y-2 translate-x-2"></div>
                  <img
                    src={teamMember1Image}
                    alt="Oyeka Amara Kate"
                    className="rounded-full object-cover shadow-lg relative z-10 w-56 h-56 border-4 border-white"
                  />
                  <motion.div
                    className="absolute inset-0 border-4 border-yellow-400/50 rounded-full"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                  />
                </div>

                <h3 className="text-3xl font-bold text-green-700 mb-2">Oyeka Amara Kate</h3>
                <p className="text-yellow-600 mb-4 text-xl">Lead Mentor</p>

                <div className="flex gap-4 mt-4">
                  {[
                    { name: "Facebook", icon: <Facebook size={20} />, link: "https://www.facebook.com/ama.kate.3110" },
                    { name: "Instagram", icon: <Instagram size={20} />, link: "https://www.instagram.com/officialkate_kenstar/" },
                    { name: "LinkedIn", icon: <Linkedin size={20} />, link: "https://www.linkedin.com/in/oyeka-amara-kate-b-sc-m-sc-fca-fcti-a9a6a91aa/" },
                    { name: "Twitter", icon: <Twitter size={20} />, link: "https://x.com/amacecekate" }
                  ].map((social, i) => (
                    <motion.a
                      key={i}
                      href={social.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center text-green-700 hover:bg-yellow-400 hover:text-green-900 transition-colors"
                      whileHover={{ y: -3 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      {social.icon}
                    </motion.a>
                  ))}
                </div>
              </div>
              <div className="md:w-2/3 text-left">
                <div className="bg-green-50 p-6 rounded-xl mb-8 relative overflow-hidden border-l-4 border-yellow-400/50">
                  <div className="absolute top-0 left-0 w-16 h-16 bg-yellow-400/20 rounded-br-3xl"></div>
                  <p className="text-green-800 font-medium italic relative z-10">
                    "My passion is helping others discover their potential and guiding them toward their goals with practical, tailored mentorship."
                  </p>
                </div>

                <div className="text-gray-700 text-justify text-lg space-y-4">
                  <p>
                    OYEKA Amara Kate is a seasoned mentor with over seven years of experience, five of which have been professional. She has mentored young children, early-career professionals, and mid-career executives, positively impacting numerous students and over ten career professionals. Amara is a sought-after speaker, frequently invited to youth forums.
                  </p>

                  <p>
                    She has been a mentor under the Women in Management, Business, and Public Service (WIMBIZ) platform for three years and has served as a referee for over 15 professionals in job applications, professional inductions, and career advancements. With vast experience in both corporate jobs and entrepreneurship, she is well-positioned as a top mentor and talent manager.
                  </p>

                  <p>
                    Amara has also lectured for three years and contributed to ICAN and CITN as a subject matter expert and examiner. She has been the lead examiner for ICAN's Management Information paper for five consecutive years. A dedicated humanist, she is a strong advocate for social equity and justice.
                  </p>
                </div>

                {/* Expertise Pills */}
                <div className="mt-8 flex flex-wrap gap-3">
                  {['Mentorship', 'Career Guidance', 'Leadership', 'Public Speaking', 'Talent Management'].map((skill, i) => (
                    <span key={i} className={`px-4 py-1 rounded-full text-sm font-medium ${i % 2 === 0 ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* Call to Action Section - NEW */}
      <section className="py-20 bg-gradient-to-r from-green-700 to-emerald-800 text-white relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAzNGM0LjQxOCAwIDgtMy41ODIgOC04cy0zLjU4Mi04LTgtOC04IDMuNTgyLTggOCAzLjU4MiA4IDggOHoiIHN0cm9rZT0iI2ZmZiIgc3Ryb2tlLXdpZHRoPSIxIiBvcGFjaXR5PSIwLjEiLz48L2c+PC9zdmc+')] opacity-20"></div>

        {/* Content */}
        <motion.div
          className="container mx-auto max-w-4xl text-center px-6 md:px-8 relative"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-3xl md:text-5xl font-bold mb-6">
            Ready to Begin Your Mentorship Journey?
          </h2>
          <p className="text-lg md:text-xl text-green-100 mb-10 max-w-3xl mx-auto">
            Join our community of emerging leaders and experienced mentors.
            Take the first step towards transforming your potential into lasting impact.
          </p>

          {/* Buttons */}
          <div className="flex flex-col md:flex-row gap-4 justify-center">

            {/* Book a Consultation Button */}
            <Link to="/contact">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="w-full md:w-auto px-8 py-4 bg-white text-green-700 rounded-full font-bold text-lg shadow-lg hover:bg-green-50 transition-colors flex items-center justify-center gap-2"
              >
                Book a Consultation
                <motion.span
                  animate={{ x: [0, 5, 0] }}
                  transition={{ repeat: Infinity, duration: 1.5 }}
                >
                  →
                </motion.span>
              </motion.button>
            </Link>

            {/* Explore Our Programs Button (Fixed) */}
            <Link to="/programs">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="w-full md:w-auto px-8 py-4 bg-transparent border-2 border-white text-white rounded-full font-bold text-lg hover:bg-white/10 transition-colors flex items-center justify-center gap-2"
              >
                Explore Our Programs
              </motion.button>
            </Link>

          </div>
        </motion.div>
      </section>

      {/* Close the component & export */}
    </div>
  );
};

export default About;